/**
 * PaymentPlanSection — the Move-In Payment Plan form section
 * (docs/move-in-payment-plan-plan.md; was "Security Deposit Payment Plan").
 *
 * Sits inside ConfigurePage's form area as a peer to the §3/§4/§5/§6 sections.
 *
 * Props:
 *   - form: the full LeaseFormState (read; the move-in charges, lease dates)
 *   - update: the form's update<K>(key, value) helper
 *
 * What staff decide, as before: which move-in charges go on the plan and how
 * much of each (the deposit, the carpet cleaning fee, each one-time fee — any
 * amount up to the full charge). Two styles:
 *   - Agreed (default): staff set the number of payments, the first charge
 *     date and the interval.
 *   - Tenant requested in writing (RCW 59.18.610): the schedule is set by
 *     statute — 3 equal monthly payments (2 when the lease is under 3 months),
 *     the first at move-in, the rest on the 1st of each following month.
 *
 * Validation (inline here; collectLeaseBlockingIssues blocks Send):
 *   - each on-plan amount clamped to [0, its charge]
 *   - planTotal > 0 required to leave the plan enabled
 *   - agreed: numberOfPayments in [2, 6], firstChargeDate >= leaseStartDate,
 *     intervalMonths in [1, 6], schedule span capped at 12 months (WEL-35)
 */

import type { LeaseFormState, MoveInCharge, PaymentPlanConfig } from '@/lib/leaseConfig';
import {
  computeAmountsDue,
  computeLeasePaymentSchedule,
  defaultFirstChargeDate,
  getPlanTotal,
  moveInCharges,
  paymentPlanSpanMonths,
  statutoryPaymentCount,
  PAYMENT_PLAN_MIN_PAYMENTS,
  PAYMENT_PLAN_MAX_PAYMENTS,
  PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS,
  PAYMENT_PLAN_MAX_SPAN_MONTHS,
} from '@/lib/leaseConfig';
import { formatMoneyCents } from '@/lib/money';
import { MoneyInput } from '@/components/MoneyInput';
import { IntInput } from '@/components/IntInput';

interface PaymentPlanSectionProps {
  form: LeaseFormState;
  update: <K extends keyof LeaseFormState>(key: K, value: LeaseFormState[K]) => void;
}

function longDate(ymd: string): string {
  return new Date(ymd + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function PaymentPlanSection({ form, update }: PaymentPlanSectionProps) {
  // Every one-time move-in charge with its full amount (and, with a plan, its
  // share on the plan). Chargeable animal count, not raw animalCount (RRA review
  // finding #7), so the deposit ceiling matches the server's validateLeaseState.
  const charges = moveInCharges(form);

  // Nothing to put on a plan: no deposit, no carpet fee, no one-time fee. An
  // enabled plan stays visible regardless, so staff can always turn it off (a
  // fee-only plan whose fee was removed would otherwise block Send, hidden).
  if (charges.length === 0 && !form.paymentPlanEnabled) {
    return null;
  }

  const depositTotal = charges.find((c) => c.kind === 'deposit')?.fullCents ?? 0;

  function buildDefaultPlan(): PaymentPlanConfig {
    return {
      depositOnPlan: depositTotal,
      includeCarpetFee: false,
      carpetFeeOnPlan: 0,
      feesOnPlan: {},
      numberOfPayments: 3,
      firstChargeDate: defaultFirstChargeDate(form.leaseStartDate),
      intervalMonths: PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS,
      tenantWrittenRequest: false,
    };
  }

  function handleEnableChange(checked: boolean) {
    if (checked) {
      update('paymentPlanEnabled', true);
      if (!form.paymentPlan) {
        update('paymentPlan', buildDefaultPlan());
      }
    } else {
      update('paymentPlanEnabled', false);
      update('paymentPlan', null);
    }
  }

  function updatePlan(patch: Partial<PaymentPlanConfig>) {
    if (!form.paymentPlan) return;
    update('paymentPlan', { ...form.paymentPlan, ...patch });
  }

  /** Set how much of one charge is on the plan (clamped to [0, the charge]). */
  function setOnPlan(c: MoveInCharge, cents: number) {
    const plan = form.paymentPlan;
    if (!plan) return;
    const v = Math.max(0, Math.min(c.fullCents, cents));
    if (c.kind === 'deposit') updatePlan({ depositOnPlan: v });
    else if (c.kind === 'carpet') updatePlan({ carpetFeeOnPlan: v, includeCarpetFee: false });
    else updatePlan({ feesOnPlan: { ...(plan.feesOnPlan ?? {}), [c.key.slice('fee:'.length)]: v } });
  }

  const plan = form.paymentPlan;
  const written = plan?.tenantWrittenRequest === true;
  const planTotal = getPlanTotal(form);
  // Up front = what is not on the plan + any plan payment due at move-in (a
  // written-request plan's first, or an agreed plan starting at move-in).
  const atMoveIn = computeAmountsDue(form, 0).paymentsAtMoveIn.reduce((sum, p) => sum + p.amount, 0);
  const upFrontTotal = charges.reduce((sum, c) => sum + c.fullCents - c.onPlanCents, 0) + atMoveIn;

  /** The amount as stored (unclamped), so an over-allocation shows instead of hiding. */
  function storedOnPlan(c: MoveInCharge): number {
    if (!plan) return 0;
    if (c.kind === 'deposit') return plan.depositOnPlan;
    if (c.kind === 'carpet') {
      return typeof plan.carpetFeeOnPlan === 'number'
        ? plan.carpetFeeOnPlan
        : plan.includeCarpetFee
          ? c.fullCents
          : 0;
    }
    return plan.feesOnPlan?.[c.key.slice('fee:'.length)] ?? 0;
  }

  const planTotalZero = form.paymentPlanEnabled && plan !== null && planTotal === 0;
  const firstChargeTooEarly =
    !written && plan && form.leaseStartDate && plan.firstChargeDate < form.leaseStartDate;

  // WEL-35: the schedule span (first charge → last charge) must not exceed
  // PAYMENT_PLAN_MAX_SPAN_MONTHS. A written-request schedule is set by statute.
  const scheduleSpanMonths = plan && !written ? paymentPlanSpanMonths(plan) : 0;
  const scheduleTooLong = plan !== null && scheduleSpanMonths > PAYMENT_PLAN_MAX_SPAN_MONTHS;

  const schedule = plan && planTotal > 0 && !scheduleTooLong ? computeLeasePaymentSchedule(form) : [];
  const statutoryCount = statutoryPaymentCount(form.leaseTermMonths);

  return (
    <section className="card">
      <div className="card-header">
        <h3 className="card-title">Move-In Payment Plan</h3>
      </div>

      <label className="choice">
        <input
          type="checkbox"
          className="check"
          checked={form.paymentPlanEnabled}
          onChange={(e) => handleEnableChange(e.target.checked)}
        />
        <span>Use a Move-In Payment Plan</span>
      </label>

      {form.paymentPlanEnabled && plan && (
        <div className="card stack-sm" style={{ marginTop: 'var(--space-5)' }}>
          <label className="choice">
            <input
              type="checkbox"
              className="check"
              checked={written}
              onChange={(e) => updatePlan({ tenantWrittenRequest: e.target.checked })}
            />
            <span>Tenant requested this plan in writing (RCW 59.18.610)</span>
          </label>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
            {written
              ? `The schedule follows the statute: ${statutoryCount} equal monthly payments, the first at move-in` +
                `${form.leaseStartDate ? ` (${longDate(form.leaseStartDate)})` : ''}, the rest on the 1st of each following month.`
              : 'Leave unchecked when you and the tenant agreed the terms; you set the payments and dates below.'}
          </div>

          <div
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-medium)',
              color: 'var(--fg)',
              marginTop: 'var(--space-3)',
            }}
          >
            On the plan:
          </div>

          {charges.map((c) => {
            const stored = storedOnPlan(c);
            const tooHigh = stored > c.fullCents;
            return (
              <div className="field" key={c.key} data-state={tooHigh ? 'error' : undefined}>
                <label>
                  {c.label} (total {formatMoneyCents(c.fullCents)}) — on plan:
                </label>
                <MoneyInput
                  valueCents={stored}
                  onChangeCents={(cents) => setOnPlan(c, cents)}
                  ariaLabel={`${c.label} on plan`}
                />
                {tooHigh && (
                  <span className="helper" data-state="error">
                    More than the {c.label.toLowerCase()} ({formatMoneyCents(c.fullCents)}). Lower the amount on the plan.
                  </span>
                )}
              </div>
            );
          })}

          <div className="card" style={{ fontSize: 'var(--text-sm)' }}>
            <div style={{ fontWeight: 'var(--weight-medium)' }}>
              Total on plan: {formatMoneyCents(planTotal)}
            </div>
            <div style={{ marginTop: 'var(--space-1)', color: 'var(--fg-muted)' }}>
              Due up front (deposit and fees{atMoveIn > 0 ? ', with plan payment 1' : ''}):{' '}
              {formatMoneyCents(upFrontTotal)}
            </div>
          </div>

          {planTotalZero && (
            <div className="alert" data-tone="danger">
              <div className="alert-body">
                Nothing is on the plan. Put an amount of at least one charge on it, or turn the plan off.
              </div>
            </div>
          )}

          {!written && (
            <>
              <div
                className="field"
                data-state={
                  plan.numberOfPayments < PAYMENT_PLAN_MIN_PAYMENTS ||
                  plan.numberOfPayments > PAYMENT_PLAN_MAX_PAYMENTS
                    ? 'error'
                    : undefined
                }
              >
                <label>
                  Number of payments ({PAYMENT_PLAN_MIN_PAYMENTS}–{PAYMENT_PLAN_MAX_PAYMENTS})
                </label>
                <IntInput
                  min={PAYMENT_PLAN_MIN_PAYMENTS}
                  max={PAYMENT_PLAN_MAX_PAYMENTS}
                  value={plan.numberOfPayments}
                  onChange={(n) => updatePlan({ numberOfPayments: n })}
                  ariaLabel="Number of payments"
                />
                {plan.numberOfPayments < PAYMENT_PLAN_MIN_PAYMENTS && (
                  <span className="helper" data-state="error">
                    Minimum {PAYMENT_PLAN_MIN_PAYMENTS} payments.
                  </span>
                )}
                {plan.numberOfPayments > PAYMENT_PLAN_MAX_PAYMENTS && (
                  <span className="helper" data-state="error">
                    Maximum {PAYMENT_PLAN_MAX_PAYMENTS} payments.
                  </span>
                )}
              </div>

              <div className="field" data-state={firstChargeTooEarly ? 'error' : undefined}>
                <label htmlFor="first-charge-date">First Charge Date</label>
                <input
                  id="first-charge-date"
                  type="date"
                  className="input"
                  value={plan.firstChargeDate}
                  onChange={(e) => updatePlan({ firstChargeDate: e.target.value })}
                />
                {firstChargeTooEarly && (
                  <span className="helper" data-state="error">
                    First Charge Date must be on or after lease start ({form.leaseStartDate}).
                  </span>
                )}
              </div>

              <div className="field" data-state={scheduleTooLong ? 'error' : undefined}>
                <label>Interval (months, 1–6)</label>
                <IntInput
                  min={1}
                  max={6}
                  value={plan.intervalMonths}
                  onChange={(n) => updatePlan({ intervalMonths: n })}
                  ariaLabel="Interval months"
                />
                {scheduleTooLong && (
                  <span className="helper" data-state="error">
                    Schedule spans {scheduleSpanMonths} months (first to last charge), over the{' '}
                    {PAYMENT_PLAN_MAX_SPAN_MONTHS}-month maximum. Reduce the number of payments or the
                    interval.
                  </span>
                )}
              </div>
            </>
          )}

          {schedule.length > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <div
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--fg)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                Schedule preview:
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Charge Date</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((p) => (
                      <tr key={p.paymentNumber}>
                        <td>
                          {longDate(p.chargeDate)}
                          {form.leaseStartDate && p.chargeDate <= form.leaseStartDate
                            ? ' — at move-in, inside the move-in total'
                            : ''}
                        </td>
                        <td className="num">{formatMoneyCents(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--fg-muted)',
                  marginTop: 'var(--space-2)',
                }}
              >
                Total on plan: {formatMoneyCents(planTotal)} · {schedule.length} payments
                {written ? ' · set by statute' : ` · every ${plan.intervalMonths} month${plan.intervalMonths !== 1 ? 's' : ''}`}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
